<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui';
import type { ComponentPublicInstance, HTMLAttributes } from 'vue';
import type { ButtonVariants } from './variants';
import type { ControlSize } from '@/components/base/_internal/control';
import type { IconSize, IconSource } from '@/components/base/icon/';
import { Loader } from 'lucide';
import { Primitive } from 'reka-ui';
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue';
import { useControlSize } from '@/components/base/_internal/control';
import { SphIcon } from '@/components/base/icon/';
import { useDeferredRef } from '@/composables/deferred-ref';
import { cn } from '@/utils/shadcn/lib/utils';
import { useButtonDefaults } from './defaults';
import { buttonVariants } from './variants';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<Props>(), {
    as: 'button',
    variant: undefined,
    size: undefined,
    icon: undefined,
    iconRight: undefined,
    iconAi: false,
    circle: false,
    class: '',
    type: 'button',

    label: '',
    ariaLabel: '',
    title: '',

    loading: false,
    disabled: false,
    active: false,
    toggle: false,

    noActiveEffect: false,
    noMinWidth: false,
});

const emit = defineEmits<{
    click: [event: MouseEvent | PointerEvent | KeyboardEvent]
}>();

interface Props extends PrimitiveProps {
    /** Falls back to the variant a container provides (see `provideButtonDefaults`), then to `default`. */
    variant?: ButtonVariants['variant']
    /** Falls back to the size a container provides (see `SphControlProvider`), then to `lg`. */
    size?: ControlSize
    icon?: IconSource
    iconRight?: IconSource
    /** Marks `icon` as AI feature, see `SphIcon`'s `ai`. */
    iconAi?: boolean
    circle?: boolean
    class?: HTMLAttributes['class']
    type?: 'button' | 'submit' | 'reset'

    // content
    label?: string
    ariaLabel?: string
    title?: string

    // state
    loading?: boolean
    disabled?: boolean
    active?: boolean
    /**
     * Marks the button as a two-state control, so `active` is reported to assistive tech as
     * `aria-pressed`. Off by default: `active` is mostly a visual state, and claiming a pressed
     * state a button does not have makes every plain button announce as a toggle — and conflicts
     * outright where the call site already carries its own state, e.g. `aria-current="page"`.
     */
    toggle?: boolean

    // ux
    noActiveEffect?: boolean
    noMinWidth?: boolean
}

const slots = useSlots();

// loading state with start-delay to prevent flickering on fast operations
const internalLoading = useDeferredRef(() => props.loading, { delayOn: 600, delayOff: 0 });

const iconType = computed<'loader' | 'icon' | null>(() => {
    if (internalLoading.value) {
        return 'loader';
    }
    if (props.icon) {
        return 'icon';
    }
    return null;
});

const iconKey = computed<string>(() => {
    if (internalLoading.value) {
        return 'loader';
    }
    if (props.icon) {
        return JSON.stringify(props.icon);
    }
    return '';
});

const iconOnly = computed<boolean>(() => {
    return !!(props.icon) && !props.label && !slots.default;
});

const resolvedSize = useControlSize(() => props.size, 'lg');

// Only a <button> knows `disabled`. Anywhere else Vue writes the attribute as is, `disabled="false"`
// included, and the legacy pages' jQuery UI tooltip, which skips `[disabled]`, leaves that element to
// the browser's native one.
const isNativeButton = computed(() => props.as === 'button');

const buttonDefaults = useButtonDefaults();
const resolvedVariant = computed<ButtonVariants['variant']>(() => props.variant ?? buttonDefaults.value.variant ?? 'default');

const sizeProcessed = computed<ButtonVariants['size']>(() => {
    if (iconOnly.value) {
        return (`icon-${resolvedSize.value}`) as ButtonVariants['size'];
    }

    return resolvedSize.value as ButtonVariants['size'];
});

const iconSizes: Record<ControlSize, IconSize> = { sm: 'xs', md: 'sm', lg: 'md' };
const iconSize = computed<IconSize>(() => iconSizes[resolvedSize.value]);

// Ripple effect
const buttonRef = ref<ComponentPublicInstance | null>(null);
const rippleContainerRef = ref<HTMLElement | null>(null);

function createRipple(event: MouseEvent | KeyboardEvent) {
    if (props.loading) {
        return;
    }
    const container = rippleContainerRef.value;
    if (!container) {
        return;
    }

    const button = buttonRef.value?.$el as HTMLElement | undefined;
    if (!button) {
        return;
    }
    const rect = button.getBoundingClientRect();

    let size = Math.max(rect.width, rect.height);
    let x;
    let y;

    if (iconOnly.value || event instanceof KeyboardEvent) {
        size *= 2;
        x = (rect.width - size) / 2;
        y = (rect.height - size) / 2;
    } else {
        size *= 2;
        x = event.clientX - rect.left - size / 2;
        y = event.clientY - rect.top - size / 2;
    }

    const ripple = document.createElement('span');
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.className = `ripple-wave ${size < 90 ? 'fast' : ''}`;

    container.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
}

// Event handlers
function handlePointerDown(event: PointerEvent) {
    if (props.noActiveEffect || props.loading) {
        return;
    }
    createRipple(event);
}

// A keyup only counts when its keydown landed on this button too. Otherwise a keystroke that moves
// focus here — Enter opening a panel whose close button takes focus — would activate it on release.
let pressedKey: string | null = null;

function isActivationKey(key: string): boolean {
    return key === 'Enter' || key === ' ';
}

// The browser already turns these keys into a click on its own: Enter and Space on a <button>,
// Enter on a link. Emitting another one on keyup would activate the button twice.
function clicksNatively(element: Element, key: string): boolean {
    if (element instanceof HTMLButtonElement) {
        return true;
    }
    return key === 'Enter' && element instanceof HTMLAnchorElement && element.hasAttribute('href');
}

function handleKeyDown(event: KeyboardEvent) {
    pressedKey = isActivationKey(event.key) ? event.key : null;
}

function handleKeyUp(event: KeyboardEvent) {
    const wasPressedHere = pressedKey === event.key;
    pressedKey = null;

    if (props.loading || !isActivationKey(event.key) || !wasPressedHere) {
        return;
    }

    if (!props.noActiveEffect) {
        createRipple(event);
    }

    if (!clicksNatively(event.currentTarget as Element, event.key)) {
        emit('click', event);
    }
}

function handleClick(event: MouseEvent) {
    if (props.loading) {
        // block native action (form submit / navigation) while busy
        event.preventDefault();
        return;
    }
    emit('click', event);
}

// Icon, label and loader each sit in a `<Transition>` of their own rather than one transition-group:
// a transition-group measures every item with `getBoundingClientRect()` on each of its renders, and
// a size change re-renders every icon. On a page of many buttons — a table switching density — that
// forced a full-page layout per button. The items that stay put still slide aside for one entering
// or leaving (the FLIP a transition-group would do); that is done by hand here, measuring only when
// the content actually changes.
const contentShape = computed(() => [
    iconType.value,
    iconKey.value,
    props.label ? 'label' : '',
    slots.default ? 'slot' : '',
    props.iconRight ? 'right' : '',
].join('|'));

// An empty `<Transition>` costs as much to mount as one with something in it, and most buttons never
// gain a label or a right icon they did not start with: in a table of icon buttons, the two empty ones
// were a third of what each button took to mount. So those two are rendered only once their item has
// been there, and then kept, for it to leave through. One that turns up after mount comes in through
// `appear`, which plays the same enter.
let isMounted = false;
onMounted(() => {
    isMounted = true;
});

function latch(): (present: unknown) => boolean {
    let seen = false;
    return (present) => (seen ||= !!present);
}

const keepsLabelTransition = latch();
const keepsIconRightTransition = latch();

function appearsLate(): boolean {
    return isMounted;
}

const itemPositions = new Map<HTMLElement, number>();
const moveEnds = new WeakMap<HTMLElement, () => void>();

function contentItems(): HTMLElement[] {
    const el = buttonRef.value?.$el as HTMLElement | undefined;
    if (!el) {
        return [];
    }
    return Array.from(el.children).filter((child): child is HTMLElement => child.classList.contains('item')
        && !child.classList.contains('smooth-list-leave-active'));
}

function playMove(item: HTMLElement, from: number) {
    moveEnds.get(item)?.();

    const offset = from - item.getBoundingClientRect().left;
    if (!offset) {
        return;
    }

    const style = item.style;
    style.transitionDuration = '0s';
    style.transform = `translateX(${offset}px)`;
    void item.offsetWidth; // force reflow
    item.classList.add('smooth-list-move');
    style.transform = style.transitionDuration = '';

    const end = (event?: TransitionEvent) => {
        if (event && (event.target !== item || !event.propertyName.endsWith('transform'))) {
            return;
        }
        item.removeEventListener('transitionend', end);
        item.classList.remove('smooth-list-move');
        moveEnds.delete(item);
    };
    item.addEventListener('transitionend', end);
    moveEnds.set(item, end);
}

// `pre` reads the old layout, `post` the new one. A button hidden via `display: none` has no box
// to move from, so it is skipped rather than having its items fly in from the viewport origin.
watch(contentShape, () => {
    itemPositions.clear();
    for (const item of contentItems()) {
        const rect = item.getBoundingClientRect();
        if (rect.width || rect.height) {
            itemPositions.set(item, rect.left);
        }
    }
}, { flush: 'pre' });

watch(contentShape, () => {
    for (const item of contentItems()) {
        const from = itemPositions.get(item);
        if (from !== undefined) {
            playMove(item, from);
        }
    }
    itemPositions.clear();
}, { flush: 'post' });

// Animate width changes when toggling between icon and label
watch([iconType, iconKey], async () => {
    const instance = buttonRef.value;
    if (!instance) {
        return;
    }
    const el = instance.$el as HTMLElement;

    // Freeze at current visual width (handles mid-animation interrupts)
    el.classList.add('freeze-transition');
    const oldWidth = el.offsetWidth;
    el.style.width = `${oldWidth}px`;

    await nextTick();

    // Measure the width it settles at: without an inline width, as the transitionend handler leaves it
    el.classList.add('is-resizing');
    el.style.removeProperty('width');
    const newWidth = el.offsetWidth;

    if (oldWidth === newWidth) {
        el.style.width = '';
        el.classList.remove('is-resizing', 'freeze-transition');
        return;
    }

    // Reset to old width and animate to new
    el.style.width = `${oldWidth}px`;
    void el.offsetWidth; // force reflow
    el.classList.remove('freeze-transition');
    el.style.width = `${newWidth}px`;

    el.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'width') {
            el.style.width = '';
            el.classList.remove('is-resizing');
        }
    }, { once: true });
}, { flush: 'pre' });
</script>

<template>
    <Primitive
        ref="buttonRef"
        data-slot="button"
        :as="props.as"
        :class="cn(
            buttonVariants({
                variant: resolvedVariant,
                size: sizeProcessed,
                circle: iconOnly && props.circle,
            }),
            props.class,
            {
                'sm:min-w-32': !iconOnly && !props.noMinWidth,
                'icon-only': iconOnly,
                'has-icon': !iconOnly && iconKey,
                active,
            },
        )"
        :type="isNativeButton ? props.type : undefined"
        :disabled="isNativeButton ? props.disabled : undefined"
        :aria-label="props.ariaLabel"
        :aria-pressed="props.toggle ? (props.active ? 'true' : 'false') : undefined"
        :aria-busy="props.loading ? 'true' : 'false'"
        :title="iconOnly ? props.title : undefined"
        @pointerdown="handlePointerDown"
        @keydown="handleKeyDown"
        @keyup="handleKeyUp"
        @click="handleClick"
    >
        <transition name="smooth-list">
            <span
                v-if="iconType"
                :key="iconType"
                class="item"
            >
                <SphIcon
                    v-if="internalLoading"
                    :icon="Loader"
                    :size="iconSize"
                    class="[&>svg]:animate-[spin_1.25s_linear_infinite]"
                />
                <transition
                    v-else
                    name="quick-switch"
                >
                    <SphIcon
                        v-if="props.icon"
                        :key="iconKey"
                        :icon="props.icon"
                        :ai="props.iconAi"
                        :size="iconSize"
                        :aria-label="iconOnly ? props.ariaLabel : undefined"
                    />
                </transition>
            </span>
        </transition>
        <transition
            v-if="keepsLabelTransition(props.label || $slots.default)"
            name="smooth-list"
            :appear="appearsLate()"
        >
            <span
                v-if="props.label || $slots.default"
                data-label
                class="item"
            >
                <slot>{{ props.label }}</slot>
            </span>
        </transition>
        <transition
            v-if="keepsIconRightTransition(iconRight)"
            name="smooth-list"
            :appear="appearsLate()"
        >
            <span
                v-if="iconRight && !props.loading"
                :key="`${iconKey}-right`"
                class="item"
            >
                <SphIcon
                    :icon="iconRight"
                    :size="iconSize"
                />
            </span>
        </transition>
        <div
            ref="rippleContainerRef"
            class="ripple-container"
            aria-hidden="true"
        ></div>
    </Primitive>
</template>

<style scoped>
.quick-switch-enter-from {
    opacity: 0;
    transform: scale(1.2) translate(0, -10%);
}

.quick-switch-leave-to {
    opacity: 0;
    transform: scale(0.8) translate(0, 10%);
}

.quick-switch-enter-to,
.quick-switch-leave-from {
    opacity: 1;
    transform: scale(1) translate(0, 0);
}

.quick-switch-enter-active,
.quick-switch-leave-active {
    transition:
        opacity 0.4s,
        transform 0.4s;
}

.quick-switch-leave-active {
    position: absolute;
}

.item {
    display: inline-flex;
    align-items: center;
}

.smooth-list-enter-from {
    opacity: 0;
    transform: translate(50%, 0);

    &.right {
        transform: translate(-50%, 0);
    }
}

.smooth-list-leave-to {
    opacity: 0;
    transform: translate(-100%, 0);

    &.right {
        transform: translate(100%, 0);
    }
}

.smooth-list-enter-to,
.smooth-list-leave-from {
    opacity: 1;
    transform: translate(0, 0);
}

.smooth-list-enter-active,
.smooth-list-leave-active {
    transition: all 0.4s var(--ease-out-cubic);
}

.smooth-list-leave-active {
    position: absolute !important;
}

.smooth-list-move {
    transition: all 0.3s var(--ease-out-cubic);
}

.is-resizing {
    overflow: hidden;
    transition: width 0.3s var(--ease-out-cubic);
}

.freeze-transition {
    transition: none !important;
}

.ripple-container {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: inherit;
    pointer-events: none;
}
</style>

<style>
/*
 * The styleguide's reset hands a visited, hovered, active or focused link the colour around it, outweighing
 * the variant's text utility; this takes the variant's colour back. One step above the reset and below a
 * state utility such as `hover:text-white`; a colour passed in `class` is not known here and needs a `!`.
 */
html a:where(.sph-button):is(:visited, :hover, :active, :focus) {
    color: var(--sph-button-color);
}

.ripple-wave {
    position: absolute;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.25;
    transform: scale(0);
    pointer-events: none;
    animation: ripple-expand 0.4s var(--ease-in-quad) forwards;

    &.fast {
        opacity: 0.5;
        animation-duration: 0.4s;
        animation-timing-function: var(--ease-in-quad);
    }
}

@keyframes ripple-expand {
    to {
        transform: scale(1);
        opacity: 0;
    }
}
</style>
